import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
  signup as signupRequest,
  type GreenUnisonUser,
  type LoginInput,
  type SignupInput,
} from "../lib/greenunisonApi";
import { getAuthToken, setAuthToken } from "../lib/http";

interface AuthContextValue {
  user: GreenUnisonUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login(input: LoginInput): Promise<GreenUnisonUser | null>;
  signup(input: SignupInput): Promise<GreenUnisonUser | null>;
  logout(): Promise<void>;
  refreshUser(): Promise<GreenUnisonUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<GreenUnisonUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshUser = useCallback(async () => {
    const currentToken = getAuthToken();
    setToken(currentToken);

    if (!currentToken) {
      setUser(null);
      return null;
    }

    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      return currentUser;
    } catch {
      setAuthToken(null);
      setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (input: LoginInput) => {
    const result = await loginRequest(input);
    setToken(result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const signup = useCallback(async (input: SignupInput) => {
    const result = await signupRequest(input);
    setToken(result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isLoading,
      login,
      signup,
      logout,
      refreshUser,
    }),
    [isLoading, login, logout, refreshUser, signup, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);

  if (!value) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return value;
}
